import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';

import TokenSummary from 'components/buyActions/TokenSummary';

import { CommonCardPrice } from '../CommonCardPrice';
import { CommonPaymentBox, CommonPaymentBoxMode } from '../CommonPaymentBox';
import { SellCommonCard_anyCard } from './__generated__/index.graphql';

type Props = {
  card: SellCommonCard_anyCard;
};

const Root = styled(Vertical)`
  width: 100%;
`;
const Frame = styled(Horizontal)`
  width: 100%;
  padding: var(--double-unit);
  background-color: rgba(var(--c-rgb-neutral-1000), 0.07);
  border-radius: var(--unit);
`;

export const SellCommonCard = ({ card }: Props) => {
  const { showNotification } = useSnackNotificationContext();
  const [paymentStarted, setPaymentStarted] = useState(false);
  const { commonCardPoints, dropCommonCardPoints } = card;
  const fees = commonCardPoints - dropCommonCardPoints;

  return (
    <Root>
      <Horizontal>
        <CommonCardPrice price={dropCommonCardPoints} />
      </Horizontal>
      <Button
        fullWidth
        size="small"
        color="tertiary"
        onClick={() => setPaymentStarted(true)}
      >
        <FormattedMessage id="sell" defaultMessage="Sell" />
      </Button>
      <CommonPaymentBox
        mode={CommonPaymentBoxMode.SELL}
        open={paymentStarted}
        onClose={() => setPaymentStarted(false)}
        onSuccess={() => {
          showNotification('commonCardDropped');
          setPaymentStarted(false);
        }}
        subtotal={commonCardPoints}
        fees={fees}
        total={dropCommonCardPoints}
        slug={card.slug}
        sport={card.sport}
        commonCardSummaryNode={
          <Frame>
            <TokenSummary
              context="SellCommonCard"
              withoutRecentSales
              card={card}
            />
          </Frame>
        }
      />
    </Root>
  );
};

SellCommonCard.fragments = {
  anyCard: gql`
    fragment SellCommonCard_anyCard on AnyCardInterface {
      slug
      sport
      commonCardPoints
      dropCommonCardPoints
      ...TokenSummary_anyCard
    }
    ${TokenSummary.fragments.anyCard}
  ` as TypedDocumentNode<SellCommonCard_anyCard>,
};
